import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Layout } from "@/components/layout/Layout";
import { Badge } from "@/components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/authContext";
import { api, type AdminUser } from "@/lib/api";
import { Users2, ShieldAlert, Trash2, RefreshCw, Pencil } from "lucide-react";

type RoleFilter = "non_client" | "all" | "staff" | "manager" | "admin" | "contractor" | "client";
type StatusFilter = "active" | "all";

const ROLE_LABELS: Record<string, string> = {
  staff: "Staff",
  manager: "Manager",
  admin: "Admin",
  contractor: "Contractor",
  client: "Client",
};

const ROLE_COLORS: Record<string, string> = {
  staff:      "bg-blue-200 text-blue-900 border-blue-400",
  contractor: "bg-violet-200 text-violet-900 border-violet-400",
  manager:    "bg-amber-200 text-amber-900 border-amber-400",
  admin:      "bg-rose-300 text-rose-900 border-rose-500",
  client:     "bg-yellow-200 text-yellow-900 border-yellow-400",
};

function AvatarChip({ name, avatar }: { name: string; avatar: string | null }) {
  const initials = avatar || name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  return (
    <div className="flex items-center gap-2">
      <div className="w-8 h-8 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-semibold shrink-0">
        {initials}
      </div>
      <span className="font-medium">{name}</span>
    </div>
  );
}

export default function UsersPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [roleFilter, setRoleFilter] = useState<RoleFilter>("non_client");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("active");
  const [confirmDeactivate, setConfirmDeactivate] = useState<AdminUser | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<AdminUser | null>(null);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editRole, setEditRole] = useState("");
  const [editActive, setEditActive] = useState(true);
  const [editCalendarEmail, setEditCalendarEmail] = useState("");

  const isAdmin = user?.role === "admin";

  const { data: users = [], isLoading } = useQuery({
    queryKey: ["admin-users"],
    queryFn: () => api.getAdminUsers(),
    enabled: isAdmin,
  });

  const roleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: "staff" | "manager" | "admin" | "contractor" | "client" }) =>
      api.updateUserRole(userId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      toast({ title: "Role updated" });
    },
    onError: (err: Error) => {
      toast({ title: "Failed to update role", description: err.message, variant: "destructive" });
    },
  });

  const statusMutation = useMutation({
    mutationFn: ({ userId, is_active }: { userId: string; is_active: boolean }) =>
      api.updateUserStatus(userId, is_active),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      toast({ title: "Status updated" });
      setConfirmDeactivate(null);
    },
    onError: (err: Error) => {
      toast({ title: "Failed to update status", description: err.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (userId: string) => api.deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      toast({ title: "User deleted" });
      setConfirmDelete(null);
    },
    onError: (err: Error) => {
      toast({ title: "Failed to delete user", description: err.message, variant: "destructive" });
    },
  });

  const resendMutation = useMutation({
    mutationFn: (userId: string) => api.resendInvite(userId),
    onSuccess: (_data, userId) => {
      const target = users.find((u) => u.id === userId);
      toast({ title: "Invite resent", description: target ? `Sent to ${target.email}` : undefined });
    },
    onError: (err: Error) => {
      toast({ title: "Failed to resend invite", description: err.message, variant: "destructive" });
    },
  });

  const editMutation = useMutation({
    mutationFn: ({ userId, updates }: { userId: string; updates: { name?: string; email?: string; role?: string; is_active?: boolean } }) =>
      api.updateUser(userId, updates as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      toast({ title: "User updated" });
      setEditingUser(null);
    },
    onError: (err: Error) => {
      toast({ title: "Failed to update user", description: err.message, variant: "destructive" });
    },
  });

  if (!isAdmin) {
    return (
      <Layout>
        <div className="space-y-6">
          <h1 className="text-3xl font-heading font-bold">Users & Roles</h1>
          <Card className="border-dashed border-2 border-destructive/50">
            <CardContent className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <ShieldAlert className="w-12 h-12 mb-4 text-destructive/50" />
              <p className="text-lg font-medium">Access Denied</p>
              <p className="text-sm">You need administrator privileges to manage users.</p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  const filtered = users.filter((u) => {
    if (roleFilter === "non_client" && u.role === "client") return false;
    if (roleFilter !== "all" && roleFilter !== "non_client" && u.role !== roleFilter) return false;
    if (statusFilter === "active" && !u.is_active) return false;
    return true;
  });

  const handleToggleStatus = (target: AdminUser) => {
    if (target.is_active) {
      setConfirmDeactivate(target);
    } else {
      statusMutation.mutate({ userId: target.id, is_active: true });
    }
  };

  const openEdit = (u: AdminUser) => {
    setEditingUser(u);
    setEditName(u.name);
    setEditEmail(u.email);
    setEditRole(u.role);
    setEditActive(u.is_active);
    setEditCalendarEmail(u.calendar_email ?? "");
  };

  const saveEdit = () => {
    if (!editingUser) return;
    const updates: { name?: string; email?: string; role?: string; is_active?: boolean; calendar_email?: string | null } = {};
    if (editName.trim() !== editingUser.name) updates.name = editName.trim();
    if (editEmail.trim() !== editingUser.email) updates.email = editEmail.trim();
    if (editRole !== editingUser.role) updates.role = editRole;
    if (editActive !== editingUser.is_active) updates.is_active = editActive;
    const newCalEmail = editCalendarEmail.trim() || null;
    if (newCalEmail !== (editingUser.calendar_email ?? null)) updates.calendar_email = newCalEmail;
    if (Object.keys(updates).length === 0) { setEditingUser(null); return; }
    editMutation.mutate({ userId: editingUser.id, updates });
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users2 className="w-8 h-8 text-primary" />
            <div>
              <h1 className="text-3xl font-heading font-bold">Users & Roles</h1>
              <p className="text-muted-foreground">
                {users.filter(u => u.role !== "client").length} staff · {users.filter(u => u.role === "client").length} portal clients
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3">
          <Select value={roleFilter} onValueChange={(v) => setRoleFilter(v as RoleFilter)}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="non_client">All Staff</SelectItem>
              <SelectItem value="staff">Staff only</SelectItem>
              <SelectItem value="contractor">Contractors only</SelectItem>
              <SelectItem value="manager">Managers only</SelectItem>
              <SelectItem value="admin">Admins only</SelectItem>
              <SelectItem value="client">Portal clients only</SelectItem>
              <SelectItem value="all">Everyone (incl. clients)</SelectItem>
            </SelectContent>
          </Select>

          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active Only</SelectItem>
              <SelectItem value="all">All (incl. inactive)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email / Calendar</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Account / Status</TableHead>
                <TableHead className="w-48">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    Loading users...
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                    No users match the current filters.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((u) => {
                  const isSelf = u.id === user?.id;
                  return (
                    <TableRow key={u.id} className={!u.is_active ? "opacity-50" : undefined}>
                      <TableCell>
                        <AvatarChip name={u.name} avatar={u.avatar} />
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-0.5">
                          <span className="text-muted-foreground text-sm">{u.email}</span>
                          {u.role !== "client" && (
                            u.calendar_email
                              ? <span className="text-xs text-emerald-600">{u.calendar_email}</span>
                              : <span className="text-xs text-muted-foreground/50 italic">no calendar email</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {isSelf || u.role === "client" ? (
                          <Badge className={`border ${ROLE_COLORS[u.role] ?? "bg-muted text-muted-foreground"}`}>
                            {ROLE_LABELS[u.role] ?? u.role}
                          </Badge>
                        ) : (
                          <Select
                            value={u.role}
                            onValueChange={(role) =>
                              roleMutation.mutate({ userId: u.id, role: role as "staff" | "manager" | "admin" | "contractor" })
                            }
                            disabled={roleMutation.isPending}
                          >
                            <SelectTrigger className={`w-auto h-6 px-2.5 text-xs font-semibold rounded-full border focus:ring-0 focus:ring-offset-0 ${ROLE_COLORS[u.role] ?? "bg-muted"}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="staff">Staff</SelectItem>
                              <SelectItem value="contractor">Contractor</SelectItem>
                              <SelectItem value="manager">Manager</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-0.5">
                          {u.role === "client" && u.account_name && (
                            <span className="text-xs text-muted-foreground">{u.account_name}</span>
                          )}
                          {u.role === "client" && !u.account_name && (
                            <span className="text-xs text-muted-foreground italic">No linked account</span>
                          )}
                          {!u.confirmed_at ? (
                            <Badge className="bg-amber-200 text-amber-900 border border-amber-400 font-semibold">
                              Pending
                            </Badge>
                          ) : (
                            <Badge className={u.is_active
                              ? "bg-green-200 text-green-900 border border-green-400 font-semibold"
                              : "bg-red-200 text-red-900 border border-red-400 font-semibold"
                            }>
                              {u.is_active ? "Active" : "Inactive"}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {!isSelf && !u.confirmed_at && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-muted-foreground hover:text-foreground px-2"
                              title="Resend invite email"
                              onClick={() => resendMutation.mutate(u.id)}
                              disabled={resendMutation.isPending}
                            >
                              <RefreshCw className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="px-2 text-muted-foreground hover:text-foreground"
                            title="Edit user"
                            onClick={() => openEdit(u)}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          {!isSelf && u.confirmed_at && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className={u.is_active ? "text-destructive hover:text-destructive" : ""}
                              onClick={() => handleToggleStatus(u)}
                              disabled={statusMutation.isPending}
                            >
                              {u.is_active ? "Deactivate" : "Reactivate"}
                            </Button>
                          )}
                          {!isSelf && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive px-2"
                              title="Delete user"
                              onClick={() => setConfirmDelete(u)}
                              disabled={deleteMutation.isPending}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Edit user dialog */}
      <Dialog open={!!editingUser} onOpenChange={(open) => !open && setEditingUser(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input value={editName} onChange={(e) => setEditName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} />
              <p className="text-xs text-muted-foreground">Changes the login email in Supabase Auth.</p>
            </div>
            {editingUser?.role !== "client" && (
              <div className="space-y-1.5">
                <Label>Secondary calendar email <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Input
                  type="email"
                  value={editCalendarEmail}
                  onChange={(e) => setEditCalendarEmail(e.target.value)}
                  placeholder="e.g. personal@gmail.com"
                />
                <p className="text-xs text-muted-foreground">
                  When this person is added to a Zoom meeting invite, the calendar invite will go to both their work email and this address.
                </p>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Role</Label>
              {editingUser?.id === user?.id ? (
                <Input value={ROLE_LABELS[editRole] ?? editRole} disabled className="bg-muted" />
              ) : (
                <Select value={editRole} onValueChange={setEditRole}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="contractor">Contractor</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="client">Portal Client (portal-only access)</SelectItem>
                  </SelectContent>
                </Select>
              )}
              {editRole === "client" && (
                <p className="text-xs text-muted-foreground">Portal clients can only access the portal. They won't appear in staff assignment dropdowns.</p>
              )}
              {editingUser?.role === "client" && editingUser.account_name && (
                <p className="text-xs text-muted-foreground">Currently linked to: <span className="font-medium">{editingUser.account_name}</span></p>
              )}
            </div>
            {editingUser?.id !== user?.id && (
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={editActive ? "active" : "inactive"} onValueChange={(v) => setEditActive(v === "active")}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingUser(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={editMutation.isPending || !editName.trim() || !editEmail.trim()}>
              {editMutation.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(open) => !open && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {confirmDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove their account. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={buttonVariants({ variant: "destructive" })}
              onClick={() => confirmDelete && deleteMutation.mutate(confirmDelete.id)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Deactivate confirmation dialog */}
      <AlertDialog open={!!confirmDeactivate} onOpenChange={(open) => !open && setConfirmDeactivate(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate {confirmDeactivate?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will prevent them from logging in. Their time entries and project assignments are
              preserved. You can reactivate them at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={buttonVariants({ variant: "destructive" })}
              onClick={() =>
                confirmDeactivate &&
                statusMutation.mutate({ userId: confirmDeactivate.id, is_active: false })
              }
            >
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
